import { logInfo, logError } from './logtail';
import { apiClient } from './apiClient';
import { handleAxiosApiError } from './apiErrorHandler';

export type StorageProvider = 'gdrive' | 'dropbox' | 'onedrive' | 'yandex' | 'none' | 'cassette_s3';

export type MigrationStatus = 'idle' | 'in_progress' | 'completed' | 'failed';

export interface StorageConnectionInfo {
  accountEmail?: string;
  rootFolderUrl?: string;
}

export interface CassetteCloudStatus {
  available: boolean;
  eligibleForProjectStorage: boolean;
  /** @deprecated use eligibleForProjectStorage */
  eligibleForDefault?: boolean;
  usedBytes?: number;
  quotaBytes?: number;
}

export interface StorageStatus {
  storageProvider: StorageProvider | '';
  defaultStorageProvider?: StorageProvider | '';
  linkedProviders?: StorageProvider[];
  connections?: Partial<Record<StorageProvider, StorageConnectionInfo>>;
  storageLinked: boolean;
  accountEmail?: string;
  rootFolderId?: string;
  rootFolderUrl?: string;
  migrationStatus: MigrationStatus;
  enabledProviders: StorageProvider[];
  quota?: {
    usedBytes?: number;
    totalBytes?: number;
    availableBytes?: number;
    cassetteUsedBytes?: number;
  };
  warnings?: string[];
  storageQuotaBytes?: number;
  storageUsedBytes?: number;
  cassetteCloudRetentionExpiresAt?: string;
  storageOnboardingComplete?: boolean;
  eligibleForProjectStorage?: boolean;
  /** @deprecated use eligibleForProjectStorage */
  canUseCassetteCloud?: boolean;
  cassetteCloud?: CassetteCloudStatus;
  s3Ready?: boolean;
}

export const STORAGE_RETURN_KEY = 'cassette:storageReturnTo';
/** Settings hash target for cloud drive linking panel (matches element id). */
export const STORAGE_SETTINGS_SECTION_ID = 'cloud-drives';

export function storageSettingsSectionPath(): string {
  return `/settings#${STORAGE_SETTINGS_SECTION_ID}`;
}

export function transferStoragePath(opts?: {
  source?: StorageProvider | null;
  target?: StorageProvider | null;
  resume?: boolean;
}): string {
  const params = new URLSearchParams();
  if (opts?.source && opts.source !== 'none') {
    params.set('source', opts.source);
  }
  if (opts?.target && opts.target !== 'none') {
    params.set('target', opts.target);
  }
  if (opts?.resume) {
    params.set('resume', '1');
  }
  const query = params.toString();
  return query ? `/settings/transfer?${query}` : '/settings/transfer';
}

/** Return path after OAuth — settings links should land on the cloud drives panel. */
export function defaultStorageReturnPath(): string {
  const { pathname, search, hash } = window.location;
  if (pathname === '/settings') {
    return `/settings${search}#${STORAGE_SETTINGS_SECTION_ID}`;
  }
  return `${pathname}${search}${hash}`;
}
export const STORAGE_CALLBACK_HANDLING_KEY = 'cassette:storageCallbackHandling';
export const STORAGE_OAUTH_PENDING_KEY = 'cassette:storageOAuthPending';
export const MIGRATION_WIZARD_KEY = 'cassette:migrationWizard';

const STORAGE_OAUTH_PENDING_VALUES: StorageProvider[] = ['gdrive', 'dropbox', 'onedrive', 'yandex'];

export function markStorageOAuthPending(provider: StorageProvider): void {
  sessionStorage.setItem(STORAGE_OAUTH_PENDING_KEY, provider);
}

export function clearStorageOAuthPending(): void {
  sessionStorage.removeItem(STORAGE_OAUTH_PENDING_KEY);
}

export function readStorageOAuthPending(): StorageProvider | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_OAUTH_PENDING_KEY);
    if (!raw) return null;
    return STORAGE_OAUTH_PENDING_VALUES.includes(raw as StorageProvider)
      ? (raw as StorageProvider)
      : null;
  } catch {
    return null;
  }
}

export type MigrationWizardStep = 'pick' | 'oauth' | 'confirm' | 'progress' | 'done';

export interface MigrationWizardPersistedState {
  step: MigrationWizardStep;
  source: StorageProvider | null;
  target: StorageProvider | null;
}

export function readMigrationWizardState(): MigrationWizardPersistedState | null {
  try {
    const raw = sessionStorage.getItem(MIGRATION_WIZARD_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as MigrationWizardPersistedState;
  } catch {
    return null;
  }
}

export function writeMigrationWizardState(state: MigrationWizardPersistedState): void {
  sessionStorage.setItem(MIGRATION_WIZARD_KEY, JSON.stringify(state));
}

export function clearMigrationWizardState(): void {
  sessionStorage.removeItem(MIGRATION_WIZARD_KEY);
}

export function saveStorageReturnPath(path?: string) {
  const target = path ?? `${window.location.pathname}${window.location.search}`;
  sessionStorage.setItem(STORAGE_RETURN_KEY, target);
}

export function consumeStorageReturnPath(fallback = '/dashboard'): string {
  const path = sessionStorage.getItem(STORAGE_RETURN_KEY);
  sessionStorage.removeItem(STORAGE_RETURN_KEY);
  if (!path || !path.startsWith('/')) {
    return fallback;
  }
  return path;
}

export const PROVIDER_LABELS: Record<StorageProvider, string> = {
  gdrive: 'Google Drive',
  dropbox: 'Dropbox',
  onedrive: 'OneDrive',
  yandex: 'Yandex Disk',
  none: 'None',
  cassette_s3: 'Cassette Cloud',
};

export async function startStorageOAuthRedirect(
  provider: StorageProvider,
  options?: { migration?: boolean },
): Promise<void> {
  logInfo('Starting storage OAuth redirect', { provider, migration: options?.migration });
  markStorageOAuthPending(provider);
  saveStorageReturnPath(defaultStorageReturnPath());
  const params = new URLSearchParams();
  params.set('frontendUrl', window.location.origin);
  if (options?.migration) params.set('migration', 'true');
  const res = await apiClient.get<{ success: boolean; authUrl?: string; errorMessage?: string }>(
    `/storage/oauth/${provider}/start?${params.toString()}`,
  );
  const result = res.data;
  if (result.success && result.authUrl) {
    window.location.assign(result.authUrl);
    return;
  }
  clearStorageOAuthPending();
  throw new Error(result.errorMessage || 'Failed to start OAuth');
}

export async function skipStorageLink(): Promise<void> {
  await apiClient.post('/storage/skip', {});
}

export async function continueWithCassetteCloud(): Promise<void> {
  await apiClient.post('/storage/continue-cassette-cloud', {});
}

export async function disconnectStorage(): Promise<void> {
  await apiClient.post('/storage/disconnect', {});
}

export async function setDefaultStorageProvider(provider: StorageProvider): Promise<void> {
  await apiClient.patch('/storage/default', { defaultStorageProvider: provider });
}

export async function disconnectStorageProvider(provider: StorageProvider, force = false): Promise<void> {
  const q = force ? '?force=true' : '';
  await apiClient.delete(`/storage/providers/${provider}${q}`);
}

export async function fetchStorageStatus(): Promise<StorageStatus> {
  const res = await apiClient.get<{ success: boolean; status: StorageStatus }>('/storage/status');
  return res.data.status;
}

export async function fetchMigrationPreflight(sourceProvider: StorageProvider, targetProvider: StorageProvider) {
  const params = new URLSearchParams({ source: sourceProvider, target: targetProvider });
  const res = await apiClient.get<{ success: boolean; preflight: MigrationPreflight }>(
    `/storage/migrate/preflight?${params.toString()}`,
  );
  return res.data.preflight;
}

export interface MigrationPreflight {
  sourceProvider: StorageProvider;
  targetProvider: StorageProvider;
  filesTotal: number;
  bytesRequired: number;
  unknownSizeFiles: number;
  targetAvailableBytes: number;
  targetTotalBytes: number;
  targetQuotaKnown: boolean;
  sufficientSpace: boolean;
  sizeEstimateIncomplete: boolean;
}

export interface MigrationJob {
  id?: string;
  userId?: string;
  sourceProvider?: StorageProvider;
  targetProvider?: StorageProvider;
  status?: MigrationStatus | string;
  filesDone?: number;
  filesTotal?: number;
  currentFile?: string;
  errors?: string[];
}

export async function startMigration(
  sourceProvider: StorageProvider,
  targetProvider: StorageProvider,
): Promise<MigrationJob> {
  const res = await apiClient.post<{ success: boolean; job: MigrationJob }>('/storage/migrate', {
    sourceProvider,
    targetProvider,
  });
  return res.data.job;
}

export interface MigrationStatusResponse {
  job: MigrationJob | null;
  targetReady?: boolean;
  userMigrationStatus?: MigrationStatus;
}

export async function fetchMigrationStatus(): Promise<MigrationStatusResponse> {
  const res = await apiClient.get<{ success: boolean; job: MigrationJob | null; targetReady?: boolean; userMigrationStatus?: MigrationStatus }>(
    '/storage/migrate/status',
  );
  return {
    job: res.data.job ?? null,
    targetReady: res.data.targetReady,
    userMigrationStatus: res.data.userMigrationStatus,
  };
}

export async function cancelMigration(): Promise<void> {
  await apiClient.post('/storage/migrate/cancel', {});
}

export function registerStorageAuthExpiredHandler(
  unlinkCallback?: () => Promise<void>,
  navigate?: (path: string) => void,
) {
  const onStorageAuthExpired = (event: Event) => {
    const detail = (event as CustomEvent<{ code?: string; provider?: string }>).detail;
    const code = detail?.code ?? '';
    const reason = code === 'storage_auth_invalid' ? 'failed' : 'expired';
    void handleStorageReauthNeeded({
      unlink: unlinkCallback,
      navigate,
      reason,
      provider: detail?.provider ?? null,
      message:
        code === 'storage_auth_invalid'
          ? 'Your cloud connection is invalid. Reconnect the same account to continue.'
          : 'Your cloud connection expired. Reconnect to continue.',
    });
  };
  window.addEventListener('gdrive-oauth-expired', onStorageAuthExpired);
  window.addEventListener('storage-auth-expired', onStorageAuthExpired);
}

export async function handleStorageReauthNeeded(options?: {
  unlink?: () => Promise<void>;
  navigate?: (path: string) => void;
  message?: string;
  provider?: StorageProvider | string | null;
  reason?: 'expired' | 'failed';
}) {
  const message = options?.message ?? 'Your cloud connection expired. Reconnect to continue.';
  logError('Storage reauth needed', { message, provider: options?.provider });
  saveStorageReturnPath(defaultStorageReturnPath());
  try {
    if (options?.unlink) await options.unlink();
  } catch (e) {
    logError('Failed to unlink during reauth', { error: String(e) });
  }
  const params = new URLSearchParams();
  params.set('reason', options?.reason ?? 'expired');
  const provider = options?.provider ?? localStorage.getItem('storageProvider');
  if (provider && provider !== 'none') {
    params.set('provider', provider);
  }
  const path = `/auth/storage?${params.toString()}`;
  if (options?.navigate) options.navigate(path);
  else window.location.assign(path);
}

/** Normalize API error payloads and dispatch storage-auth-expired when cloud re-link is needed. */
export function notifyStorageAuthIssueFromApiError(error: unknown): boolean {
  return handleAxiosApiError(error);
}

// Legacy aliases
export const saveGDriveReturnPath = saveStorageReturnPath;
export const consumeGDriveReturnPath = consumeStorageReturnPath;

export const startGDriveOAuthRedirect = () => startStorageOAuthRedirect('gdrive');
export const registerGDriveOAuthExpiredHandler = registerStorageAuthExpiredHandler;
